// Color utilities for flattening rgba overlays into solid colors.
// Useful when CSS background-image (gradients) can't be transitioned and
// you need a pre-blended solid background-color instead.

export type RGB = { r: number; g: number; b: number };
export type RGBA = RGB & { a: number };

export const hexToRgb = (hex: string): RGB => {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16)
  };
};

export const rgbToHex = ({ r, g, b }: RGB): string =>
  "#" +
  [r, g, b]
    .map((c) => Math.round(c).toString(16).padStart(2, "0").toUpperCase())
    .join("");

// Parse "rgba(255, 255, 255, 0.54)" / "rgb(7, 31, 53)" → { r, g, b, a }
export const parseRgba = (input: string): RGBA => {
  const match = input.match(
    /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i
  );
  if (!match) throw new Error(`Invalid rgb/rgba string: ${input}`);
  return {
    r: Number(match[1]),
    g: Number(match[2]),
    b: Number(match[3]),
    a: match[4] === undefined ? 1 : Number(match[4])
  };
};

// Accepts "#RRGGBB", "#RGB", "rgb(...)" or "rgba(...)".
export const toRgba = (color: string): RGBA => {
  if (color.startsWith("#")) return { ...hexToRgb(color), a: 1 };
  return parseRgba(color);
};

/**
 * Alpha compositing (Porter-Duff "source-over"):
 *   result = overlay * α + base * (1 − α)
 *
 * Returns the solid color produced when a (possibly translucent) overlay is
 * painted on top of an opaque base.
 *
 * Example:
 *   flattenOverlay("#071F35", "rgba(255, 255, 255, 0.54)") === "#8D98A2"
 */
export const flattenOverlay = (
  base: string,
  overlay: string | RGBA
): string => {
  const baseRgb = toRgba(base);
  const ov = typeof overlay === "string" ? parseRgba(overlay) : overlay;
  const a = ov.a;
  return rgbToHex({
    r: ov.r * a + baseRgb.r * (1 - a),
    g: ov.g * a + baseRgb.g * (1 - a),
    b: ov.b * a + baseRgb.b * (1 - a)
  });
};

/**
 * Stacks multiple overlays on top of a base, in order (first item is the
 * bottom-most overlay).
 *
 *   flattenLayers("#071F35", ["rgba(255,255,255,0.5)", "rgba(0,0,0,0.2)"])
 */
export const flattenLayers = (
  base: string,
  overlays: Array<string | RGBA>
): string => overlays.reduce<string>((acc, ov) => flattenOverlay(acc, ov), base);
